<script lang="ts">
    import * as yup from 'yup'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import type { INotification } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { getContext, onDestroy, onMount } from 'svelte'
    import { fade } from 'svelte/transition'
    import type { Writable } from 'svelte/store'
    import { cardHeaderStyle } from 'hydria/context'

    interface Props {
        currentEmail: string
        saved: () => void
        close: () => void
    }

    let { currentEmail, saved, close }: Props = $props()

    let saving = $state(false)
    let confirmationSent = $state(false)

    let stoken = getContext('securityToken') as Writable<string>

    const schema = yup.object().shape({
        email: yup.string().email().required().label('Email'),
    })

    interface UserEdit {
        email: string
    }
    let orig: UserEdit = $state({
        email: currentEmail,
    })
    let edit: UserEdit = $state({ ...orig })

    // @ts-expect-error
    let emailIsValid = $derived(schema.fields.email.isValidSync(edit.email))
    let formIsValid = $derived(schema.isValidSync(edit))

    let dirty = $derived(orig.email != edit.email)

    async function saveData() {
        if (!dirty || saving) return

        saving = true

        const notifyError: INotification = {
            type: 'danger',
            title: $_('User information'),
            text: $_('The user information could not be saved.'),
        }
        let r: any
        try {
            r = await simpleFetch(
                'POST',
                '/json/security/1/email',
                {
                    email: edit.email,
                },
                { headers: { 'x-security-token': $stoken } },
            )
        } catch (e) {
            console.error('Failed to save user information:', e)
            notify(notifyError)
            focus()
        }
        if (r.data.success) {
            notify({
                type: 'success',
                title: $_('User information'),
                text: $_('An email has been sent to confirm the new email address, {emailAddress}', { emailAddress: edit.email }),
            })
            saved()
            // orig = { ...edit }
            // session.checkToken()
            confirmationSent = true
        } else {
            notify(notifyError)
            focus()
        }
        saving = false
    }

    let changeTimer: ReturnType<typeof setTimeout> | null = null
    $effect(() => {
        if (confirmationSent) {
            if (changeTimer) clearTimeout(changeTimer)
            changeTimer = setTimeout(() => {
                // changed = false
                changeTimer = null
                close()
            }, 7000)
        }
    })

    let emailElement: HTMLInputElement | undefined = $state()

    function focus() {
        if (emailElement) {
            emailElement.focus()
            emailElement.select()
        }
    }
    onMount(() => {
        focus()
    })

    let addedModalOpen = false
    if (!document.documentElement.classList.contains('modal-open')) {
        document.documentElement.classList.add('modal-open')
        addedModalOpen = true
    }

    onDestroy(() => {
        if (changeTimer) clearTimeout(changeTimer)
        if (addedModalOpen && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (!saving) {
                close()
            }
        }
    }}
/>

<div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <form
                onsubmit={(e) => {
                    e.preventDefault()
                    saveData()
                }}
            >
                <div class="card">
                    <div class={cardHeaderStyle}>{$_('Email address')}</div>
                    {#if confirmationSent}
                        <div class="card-body">
                            <p>{$_('An email should arrive shortly, containing a link. You need to follow that link in order to confirm the new email address before the change takes effect.')}</p>
                        </div>
                    {:else}
                        <div class="card-body">
                            <p>{$_('This email is used as the account user ID to view your WebDomaine account.')}</p>

                            <div class="form-group mt-3">
                                <label for="current-email" class="col-sm-4 col-form-label">{$_('Current email address')}</label>
                                <input type="email" class="form-control" bind:value={orig.email} disabled={true} id="current-email" />
                            </div>

                            <p class="mt-4">{$_('Please enter the new email address that will become your new login for your WebDomaine account.')}</p>

                            <div class="form-group mt-3">
                                <label for="email" class="col-sm-4 col-form-label">{$_('New email address')}</label>
                                <input type="email" class="form-control" id="email" bind:value={edit.email} bind:this={emailElement} disabled={saving} class:is-invalid={!emailIsValid} />
                                <span class="form-text">{$_('An email will be sent to this new address, containing a link you must follow to confirm the change.')}</span>
                            </div>
                        </div>
                        <div class="card-footer text-end">
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={saving}
                                onclick={() => {
                                    close()
                                }}>{$_('Cancel')}</button
                            >
                            <button type="submit" class="btn btn-primary" disabled={saving || !dirty || !formIsValid}>
                                {#if saving}<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                    {$_('Saving…')}
                                {:else}{$_('Submit changes')}
                                {/if}
                            </button>
                        </div>
                    {/if}
                </div>
            </form>
        </div>
    </div>
    <div
        class="modal-backdrop"
        onclick={() => {
            if (!saving) close()
        }}
        aria-hidden="true"
    ></div>
</div>
